import { createContext, SetStateAction, useMemo } from 'react';
import { IResourceInfo } from '../../Utils/KalenderTypes';
import { INyAktivitetInitialValues } from './Utils/NyAktivitetInitialValues';
import { ICalendarSelection } from '../../../../../../../BaseComponents/CalendarView/Utils/EDCalendarViewUtils';

interface IViewDbSettingsContext {
  currentView: string;
  setCurrentView: (newView: string) => void;
  overView: boolean;
  setOverView: React.Dispatch<React.SetStateAction<boolean>>;
  seeMoreFeatures: boolean;
  setSeeMoreFeatures: React.Dispatch<React.SetStateAction<boolean>>;
  currentDate: Date | undefined;
  setCurrentDate: (newDate: Date) => void;
  resources?: IResourceInfo[];
  setResources: (newResources: IResourceInfo[]) => void;
  loggedInUserResource?: IResourceInfo | undefined;
  setLoggedInUserResource: (newResource: IResourceInfo | undefined) => void;
  nyAktivitetInitialValues?: INyAktivitetInitialValues | undefined;
  getNyAktivitetInitialValues: () => INyAktivitetInitialValues | undefined;
  setNyAktivitetInitialValues: (
    newNyAktivitetInitialValues: INyAktivitetInitialValues | undefined
  ) => void;
  calendarSelection: ICalendarSelection | undefined;
  setCalendarSelection: (newSelection: ICalendarSelection | undefined) => void;
  showOutlookBookings: boolean;
  setShowOutlookBookings: React.Dispatch<React.SetStateAction<boolean>>;
  setDayCount: (dayCount: number) => void;
  dayCount: number;
  calenderReportSysOption: React.SetStateAction<string | undefined>;
  setCalenderReportSysOption: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  /** Only used in list view */
  scrollToCurrentIndexRef: React.RefObject<(() => void) | null>;
}

const defaultValues: IViewDbSettingsContext = {
  currentView: 'Week',
  setCurrentView: (newView: string): void => {},
  overView: false,
  setOverView: (value: SetStateAction<boolean>): void => {},
  seeMoreFeatures: false,
  setSeeMoreFeatures: (value: SetStateAction<boolean>): void => {},
  currentDate: new Date(),
  setCurrentDate: () => {},
  resources: undefined,
  setResources: () => {},
  loggedInUserResource: undefined,
  setLoggedInUserResource: () => {},
  nyAktivitetInitialValues: undefined,
  getNyAktivitetInitialValues: () => {
    return undefined;
  },
  setNyAktivitetInitialValues: () => {},
  calendarSelection: undefined,
  setCalendarSelection: () => {},
  showOutlookBookings: false,
  setShowOutlookBookings: (value: SetStateAction<boolean>): void => {},
  setDayCount: (dayCount: number) => {},
  dayCount: 7,
  calenderReportSysOption: undefined,
  setCalenderReportSysOption: (
    value: React.SetStateAction<string | undefined>
  ): void => {},
  scrollToCurrentIndexRef: { current: null },
};

export const ViewDbSettingsContext =
  createContext<IViewDbSettingsContext>(defaultValues);

export const ViewDbSettingsContextProvider: React.FC<
  React.PropsWithChildren<IViewDbSettingsContext>
> = props => {
  const value: IViewDbSettingsContext = useMemo(
    () => ({
      currentView: props.currentView,
      setCurrentView: props.setCurrentView,
      overView: props.overView,
      setOverView: props.setOverView,
      seeMoreFeatures: props.seeMoreFeatures,
      setSeeMoreFeatures: props.setSeeMoreFeatures,
      currentDate: props.currentDate,
      setCurrentDate: props.setCurrentDate,
      resources: props.resources,
      setResources: props.setResources,
      loggedInUserResource: props.loggedInUserResource,
      setLoggedInUserResource: props.setLoggedInUserResource,
      nyAktivitetInitialValues: props.nyAktivitetInitialValues,
      getNyAktivitetInitialValues: props.getNyAktivitetInitialValues,
      setNyAktivitetInitialValues: props.setNyAktivitetInitialValues,
      calendarSelection: props.calendarSelection,
      setCalendarSelection: props.setCalendarSelection,
      showOutlookBookings: props.showOutlookBookings,
      setShowOutlookBookings: props.setShowOutlookBookings,
      setDayCount: props.setDayCount,
      dayCount: props.dayCount,
      calenderReportSysOption: props.calenderReportSysOption,
      setCalenderReportSysOption: props.setCalenderReportSysOption,
      scrollToCurrentIndexRef: props.scrollToCurrentIndexRef,
    }),
    [
      props.currentView,
      props.setCurrentView,
      props.overView,
      props.setOverView,
      props.seeMoreFeatures,
      props.setSeeMoreFeatures,
      props.currentDate,
      props.setCurrentDate,
      props.resources,
      props.setResources,
      props.loggedInUserResource,
      props.setLoggedInUserResource,
      props.nyAktivitetInitialValues,
      props.getNyAktivitetInitialValues,
      props.setNyAktivitetInitialValues,
      props.calendarSelection,
      props.setCalendarSelection,
      props.showOutlookBookings,
      props.setShowOutlookBookings,
      props.setDayCount,
      props.dayCount,
      props.calenderReportSysOption,
      props.setCalenderReportSysOption,
      props.scrollToCurrentIndexRef,
    ]
  );
  return (
    <ViewDbSettingsContext.Provider value={value}>
      {props.children}
    </ViewDbSettingsContext.Provider>
  );
};

export { defaultValues as viewDbSettingContextDefaultValues };
